import type { VapiMessage, VapiToolCall } from '@/features/calls/types'

export interface ToolInvocation {
  callId: string | null
  name: string
  args: Record<string, unknown>
  result: Record<string, unknown> | { error: string }
  startedAt: number
  endedAt: number | null
  status: 'ok' | 'error' | 'orphaned-call' | 'orphaned-result'
}

interface PendingCall {
  message: VapiMessage
  toolCall: VapiToolCall
  name: string
  callId: string | null
}

function getResultId(m: VapiMessage): string | null {
  return m.toolCallId ?? m.tool_call_id ?? null
}

function parseResult(
  result: VapiMessage['result']
): Record<string, unknown> | { error: string } {
  if (result == null) return { error: 'No result returned' }
  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result) as unknown
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : { value: result }
    } catch {
      return { value: result }
    }
  }
  return result
}

export function parseArgs(tc: VapiToolCall): Record<string, unknown> {
  const raw = tc.function?.arguments
  if (raw == null) return {}
  if (typeof raw !== 'string') return raw as Record<string, unknown>
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return { _raw: raw }
  }
}

function orphanCall(p: PendingCall): ToolInvocation {
  return {
    callId: p.callId,
    name: p.name,
    args: parseArgs(p.toolCall),
    result: { error: 'No result returned' },
    startedAt: p.message.secondsFromStart,
    endedAt: null,
    status: 'orphaned-call',
  }
}

export function deriveToolInvocations(
  messages: VapiMessage[]
): ToolInvocation[] {
  const out: ToolInvocation[] = []
  const byId = new Map<string, PendingCall>()
  const byName = new Map<string, PendingCall[]>()

  for (const m of messages) {
    if (m.role === 'tool_calls') {
      for (const tc of m.toolCalls ?? []) {
        const name = tc.function?.name ?? 'unknown'
        const callId = tc.id ?? null
        const pending: PendingCall = { message: m, toolCall: tc, name, callId }
        if (callId) {
          const displaced = byId.get(callId)
          if (displaced) out.push(orphanCall(displaced))
          byId.set(callId, pending)
        } else {
          const queue = byName.get(name) ?? []
          queue.push(pending)
          byName.set(name, queue)
        }
      }
      continue
    }

    if (m.role === 'tool_call_result') {
      const resultId = getResultId(m)
      const result = parseResult(m.result)
      let pending: PendingCall | undefined

      if (resultId && byId.has(resultId)) {
        pending = byId.get(resultId)
        byId.delete(resultId)
      } else {
        // Result side dropped the id (Vapi has done this) — try the name
        // FIFO queue first, then scan byId for the oldest matching name.
        // Map preserves insertion order, which is startedAt order.
        pending = byName.get(m.name ?? 'unknown')?.shift()
        if (!pending && m.name) {
          for (const [id, p] of byId.entries()) {
            if (p.name === m.name) {
              pending = p
              byId.delete(id)
              break
            }
          }
        }
      }

      if (!pending) {
        out.push({
          callId: resultId,
          name: m.name ?? 'unknown',
          args: {},
          result,
          startedAt: m.secondsFromStart,
          endedAt: m.secondsFromStart,
          status: 'orphaned-result',
        })
        continue
      }

      const errored =
        'error' in result &&
        typeof result.error === 'string' &&
        result.error.length > 0
      out.push({
        callId: pending.callId,
        name: pending.name,
        args: parseArgs(pending.toolCall),
        result,
        startedAt: pending.message.secondsFromStart,
        endedAt: m.secondsFromStart,
        status: errored ? 'error' : 'ok',
      })
    }
  }

  for (const pending of byId.values()) out.push(orphanCall(pending))
  for (const queue of byName.values())
    for (const p of queue) out.push(orphanCall(p))

  return out.toSorted((a, b) => a.startedAt - b.startedAt)
}
